import type { WorkOrderStatusValue } from './statuses';

/**
 * The Work Order's own rules beside the rate model (piece 22): the footer
 * action each status offers, read by the record page and held by a test, and
 * the invoice block a travel figure that never resolved raises.
 */
export const WORK_ORDER_ACTIONS = ['schedule', 'submit', 'start', 'complete', 'close', 'closeWithoutBilling', 'backToDraft', 'createFollowUp', 'void', 'hold', 'resume', 'delete'] as const;
export type WorkOrderAction = (typeof WORK_ORDER_ACTIONS)[number];

/**
 * The status each footer action moves the Work Order to, where it is a status
 * move; Schedule, the follow-up and Close Without Billing are writes of their
 * own, the last because it records why the job bills nothing (decision 2611).
 */
export const ACTION_TARGET_STATUS: Readonly<Partial<Record<WorkOrderAction, WorkOrderStatusValue>>> = {
  start: 'InProgress',
  complete: 'Complete',
  close: 'Closed',
  backToDraft: 'Draft',
  void: 'Void',
};

/**
 * 🔨 The footer offers the moves the Work Order transition table allows from
 * the stored status and nothing else: Draft schedules, Dispatched starts,
 * InProgress completes, Complete closes, and a follow-up is
 * created from a Complete or Closed order. Void stands on a Draft, Scheduled
 * or Dispatched order, and on an In Progress order that carries nothing Cancel Work Order refuses (a job started
 * by mistake); a Complete, Closed or Void order offers no Void, and a follow-up is never raised from a job that
 * has not finished.
 */
export function workOrderActions(status: WorkOrderStatusValue, facts: { approvalStatus?: string | null; hasLines?: boolean; submitHolds?: boolean; held?: boolean; owesBilling?: boolean; carriesWork?: boolean; deletesAnyway?: boolean } = {}): WorkOrderAction[] {
  const moves = statusActions(status, facts);
  // 🔒 Decision 12970: for the `admin.operations` holder Delete stands in every status, opening the window that offers Delete Anyway.
  return facts.deletesAnyway && !moves.includes('delete') ? [...moves, 'delete'] : moves;
}

function statusActions(status: WorkOrderStatusValue, facts: { approvalStatus?: string | null; hasLines?: boolean; submitHolds?: boolean; held?: boolean; owesBilling?: boolean; carriesWork?: boolean }): WorkOrderAction[] {
  switch (status) {
    case 'Draft':
      // 🔒 Submit for approval stands where the gate's dry run would hold the job, and never where the press would land Not required again (T223; decision 0713).
      // 🔒 Decision 12700: Void reads Cancel Work Order, and Delete stands until a visit goes to a crew; the server refuses a job carrying anything by name.
      return ['schedule', ...(facts.approvalStatus === 'NotRequired' && facts.hasLines && facts.submitHolds !== false ? (['submit'] as WorkOrderAction[]) : []), 'void', 'delete'];
    case 'Scheduled':
      // 🔒 Decision 4340: booking a crew on the visit is the dispatch, so the footer carries no Dispatch.
      return ['backToDraft', 'void', 'delete'];
    case 'Dispatched':
      // 🔒 Decision 4340: a Dispatched job goes back by Edit Visit or Cancel Visit on its visit, never by a footer move.
      return ['start', 'void'];
    case 'InProgress':
      // 🔒 A job in progress is put On Hold with a reason and resumed, neither a status move (T1300; piece 29).
      // 🔒 Cancel Work Order stands only while the job carries nothing the server refuses it for: a visit stamp, a time entry, stock, an invoice or a posted entry. `carriesWork` is that one fact, read where `cancelProblem` reads it, so a job started by mistake can still be cancelled (decision 12700; T3830; the EOD86C review).
      return [facts.held ? 'resume' : 'hold', 'complete', ...(facts.carriesWork === false ? (['void'] as WorkOrderAction[]) : [])];
    case 'Complete':
      // 🔒 Decision 2611 (T1170): a job with hours, parts or a trip not invoiced also offers Close Without Billing, which asks why.
      return ['close', ...(facts.owesBilling ? (['closeWithoutBilling'] as WorkOrderAction[]) : []), 'createFollowUp'];
    case 'Closed':
      return ['createFollowUp'];
    case 'Void':
    default:
      return [];
  }
}

/** The label each action reads on the footer; the string table carries the words and this names the keys. */
export const WORK_ORDER_ACTION_KEYS: Readonly<Record<WorkOrderAction, `workOrder.action.${WorkOrderAction}`>> = {
  schedule: 'workOrder.action.schedule',
  submit: 'workOrder.action.submit',
  start: 'workOrder.action.start',
  complete: 'workOrder.action.complete',
  close: 'workOrder.action.close',
  closeWithoutBilling: 'workOrder.action.closeWithoutBilling',
  backToDraft: 'workOrder.action.backToDraft',
  createFollowUp: 'workOrder.action.createFollowUp',
  void: 'workOrder.action.void',
  hold: 'workOrder.action.hold',
  resume: 'workOrder.action.resume',
  delete: 'workOrder.action.delete',
};

/** A Work Order is priced for the customer while it is open; Closed and Void read only. */
export function workOrderEditable(status: WorkOrderStatusValue): boolean {
  return status !== 'Closed' && status !== 'Void';
}

/**
 * 🔒 The shelf a part fitted at a job comes off (decision 0530): the Work
 * Order's own travel origin site, then the dispatch setting's default. Where
 * neither names a site the add is refused rather than guessing a shelf, because
 * a movement written against the wrong site is wrong in two places at once.
 */
export function materialShelfOf(facts: { travelOriginSiteId: string | null; defaultOriginSiteId: string | null }): string | null {
  return facts.travelOriginSiteId ?? facts.defaultOriginSiteId ?? null;
}

/** 🔨 The refusal a job with no shelf reads: it names the two places a site could have come from, so the reader knows which to fix. */
export function materialShelfProblem(siteId: string | null): string | null {
  if (siteId) return null;
  return 'this job names no origin site and no default dispatch site is set, so there is no shelf for the part to come off';
}

/**
 * 🔨 The picker refuses a part the shelf cannot cover, naming the site and what
 * it holds (decision 0530). A movement past the layers posts cost for units
 * that were never there and drives the site negative.
 */
export function materialStockProblem(facts: { siteName: string; covered: number | null; fitting: number }): string | null {
  if (facts.covered === null) return `nothing of this part is on hand at ${facts.siteName}, so there is nothing to fit from it`;
  if (facts.covered < facts.fitting) return `${facts.siteName} holds ${facts.covered} of this part and this line fits ${facts.fitting}`;
  return null;
}

/** 🔨 A material line written before decision 0530 names no movement, so Remove takes the line alone and the shelf it never moved stays unmoved. */
export function materialReversesStock(line: { stockMovementId: string | null }): boolean {
  return line.stockMovementId !== null;
}
